"use client";

import { useState, useEffect } from "react";
import { Loader2, Check, Circle } from "lucide-react";

interface AnalyzeProgressPreviewProps {
  className?: string;
}

interface PreviewStep {
  id: string;
  title: string;
  status: "pending" | "loading" | "completed";
}

export function AnalyzeProgressPreview({
  className = "",
}: AnalyzeProgressPreviewProps) {
  const [steps, setSteps] = useState<PreviewStep[]>([
    { id: "site-metadata", title: "Analyzing Site", status: "pending" },
    { id: "domain-rating", title: "Checking Domain Rating", status: "pending" },
    { id: "domain-analysis", title: "Scanning Security", status: "pending" },
    { id: "chrome-ux", title: "Measuring UX", status: "pending" },
    { id: "pagespeed", title: "Testing Speed", status: "pending" },
  ]);

  useEffect(() => {
    // loop through steps for preview: first step 2s, each next +1s, then repeat
    let isMounted = true;
    const baseMs = 2000; // 2 seconds for first step
    const run = async () => {
      while (isMounted) {
        // reset all to pending at the start of a cycle
        setSteps((prev) => prev.map((s) => ({ ...s, status: "pending" })));
        for (let i = 0; i < steps.length; i++) {
          if (!isMounted) return;
          // set current loading and previous completed
          setSteps((prev) => {
            const next = [...prev];
            if (i > 0) next[i - 1].status = "completed";
            next[i].status = "loading";
            return next;
          });
          const delay = baseMs + i * 1000; // +1s per step
          await new Promise((r) => setTimeout(r, delay));
        }
        if (!isMounted) return;
        // mark last completed and small pause before next cycle
        setSteps((prev) => {
          const next = [...prev];
          next[next.length - 1].status = "completed";
          return next;
        });
        await new Promise((r) => setTimeout(r, 5000));
      }
    };
    run();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`w-full mx-auto max-w-[200px] ${className}`}>
      <div className="space-y-4">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`flex items-center gap-4 transition-all duration-700 ease-out transform ${
              step.status === "loading"
                ? "opacity-100 scale-100 translate-x-0"
                : step.status === "completed"
                ? "opacity-70 scale-98 translate-x-0"
                : "opacity-40 scale-95 translate-x-0"
            }`}
            style={{ transitionDelay: `${index * 60}ms` }}
          >
            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
              {step.status === "loading" ? (
                <div className="relative">
                  <div className="w-6 h-6 rounded-full bg-primary/20 animate-pulse" />
                  <Loader2 className="w-4 h-4 animate-spin text-primary absolute top-1 left-1" />
                </div>
              ) : step.status === "completed" ? (
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center animate-in zoom-in duration-300"
                  style={{ backgroundColor: "#10b981" }}
                >
                  <Check className="w-4 h-4 text-white" />
                </div>
              ) : (
                <Circle className="w-6 h-6 text-muted-foreground/40" />
              )}
            </div>
            <div className="flex-1">
              <div
                className={`text-base transition-all duration-500 ${
                  step.status === "loading"
                    ? "text-foreground font-semibold"
                    : step.status === "completed"
                    ? "text-foreground font-medium"
                    : "text-muted-foreground/60 font-normal"
                }`}
              >
                {step.title}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
